import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { cn } from '@/lib/cn';
import { useMapColorStore, useMapStyle, type MapColorKey, type MapPaletteKey } from './mapColors';

const OPEN_KEY = 'forest.map.legendOpen';

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) !== '0';
  } catch {
    return true;
  }
}

/**
 * What each colour and shape on the map means, with a switch between the
 * two palettes and a colour picker on every editable swatch. Portaled into
 * Leaflet's own bottom-left corner so it stacks with the controls there.
 */
export function MapLegend() {
  const { t } = useTranslation(['common']);
  const map = useMap();
  const [corner, setCorner] = useState<HTMLElement | null>(null);
  const [open, setOpenState] = useState(readOpen);
  const ref = useRef<HTMLDivElement | null>(null);
  const style = useMapStyle();
  const overrides = useMapColorStore((s) => s.overrides);
  const setPalette = useMapColorStore((s) => s.setPalette);
  const setColor = useMapColorStore((s) => s.setColor);
  const resetColors = useMapColorStore((s) => s.resetColors);
  const [draft, setDraft] = useState<{ key: MapColorKey; value: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const { colors } = style;

  // The picker fires on every drag step; each saved colour repaints the
  // polygon tiles, so only the colour the user settles on is saved.
  const pick = (key: MapColorKey, value: string) => {
    setDraft({ key, value });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setColor(key, value);
      setDraft(null);
    }, 250);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  const customised = Object.keys(overrides[style.palette] ?? {}).length > 0;

  const setOpen = (v: boolean) => {
    setOpenState(v);
    try {
      localStorage.setItem(OPEN_KEY, v ? '1' : '0');
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    setCorner(map.getContainer().querySelector<HTMLElement>('.leaflet-bottom.leaflet-left'));
  }, [map]);

  // Clicks on the legend must not reach the map, which would open a polygon.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
  }, [corner]);

  if (!corner) return null;

  const title = t('common:map.legend.title', { defaultValue: 'Legendă' });

  const picker = (key: MapColorKey, swatch: React.ReactNode, label: string) => (
    <li className="flex items-center gap-2">
      <label
        className="relative inline-flex h-4 w-5 flex-shrink-0 cursor-pointer items-center justify-center"
        title={t('common:map.legend.changeColor', { defaultValue: 'Schimbă culoarea' })}
      >
        {swatch}
        <input
          type="color"
          value={draft?.key === key ? draft.value : colors[key]}
          onChange={(e) => pick(key, e.target.value)}
          aria-label={`${label}: ${t('common:map.legend.changeColor', { defaultValue: 'Schimbă culoarea' })}`}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </label>
      <span className="text-slate-700 dark:text-slate-200">{label}</span>
    </li>
  );

  const paletteButton = (key: MapPaletteKey, label: string) => (
    <button
      type="button"
      onClick={() => setPalette(key)}
      aria-pressed={style.palette === key}
      className={cn(
        'flex-1 rounded px-2 py-0.5 font-medium transition-colors',
        style.palette === key
          ? 'bg-forest-600 text-white'
          : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700',
      )}
    >
      {label}
    </button>
  );

  return createPortal(
    <div ref={ref} className="leaflet-control">
      {open ? (
        <div className="w-56 rounded-md border border-slate-200 bg-white/95 p-2 text-[11px] shadow-md backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800/95">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
              {title}
            </span>
            <button
              type="button"
              className="text-base leading-none text-slate-400 hover:text-slate-600"
              onClick={() => setOpen(false)}
              aria-label={t('common:action.close')}
            >
              &times;
            </button>
          </div>

          <div
            role="group"
            aria-label={t('common:map.legend.palette', { defaultValue: 'Paletă' })}
            className="mb-2 flex gap-1 rounded border border-slate-200 p-0.5 dark:border-slate-600"
          >
            {paletteButton(
              'classic',
              t('common:map.legend.paletteClassic', { defaultValue: 'Hartă' }),
            )}
            {paletteButton(
              'satellite',
              t('common:map.legend.paletteSatellite', { defaultValue: 'Satelit' }),
            )}
          </div>

          <ul className="space-y-1.5">
            {picker(
              'parcelLine',
              <span
                className="h-3.5 w-5 rounded-sm border-2"
                style={{
                  borderColor: colors.parcelLine,
                  backgroundColor: hexAlpha(colors.parcelFill, style.parcelFillOpacity),
                  boxShadow: style.parcelHalo ? `0 0 0 1px ${style.parcelHalo}` : undefined,
                }}
              />,
              t('common:map.legend.parcelLine', { defaultValue: 'Poligon (contur)' }),
            )}
            {picker(
              'parcelFill',
              <span
                className="h-3.5 w-5 rounded-sm border border-slate-300"
                style={{ backgroundColor: colors.parcelFill }}
              />,
              t('common:map.legend.parcelFill', { defaultValue: 'Poligon (umplere)' }),
            )}
            {picker(
              'subparcel',
              <span
                className="h-3.5 w-5 rounded-sm border-2"
                style={{
                  borderColor: colors.subparcel,
                  backgroundColor: hexAlpha(colors.subparcel, 0.2),
                }}
              />,
              t('common:map.legend.subparcel', { defaultValue: 'Subpoligon' }),
            )}
            <li className="flex items-center gap-2">
              <span className="inline-flex h-4 w-5 flex-shrink-0 items-center justify-center">
                <span
                  className="h-3.5 w-5 rounded-sm border-2 border-dashed"
                  style={{ borderColor: colors.subparcel }}
                />
              </span>
              <span className="text-slate-700 dark:text-slate-200">
                {t('common:map.legend.subparcelUnattached', {
                  defaultValue: 'Subpoligon fără poligon (linie întreruptă)',
                })}
              </span>
            </li>
            {picker(
              'photo',
              <span
                className="h-3 w-3 rounded-full"
                style={{
                  backgroundColor: colors.photo,
                  border: `2px solid ${style.photoRing ?? colors.photo}`,
                  boxShadow: style.photoRing ? '0 0 0 1px #1f2937' : undefined,
                }}
              />,
              t('common:map.legend.photo', { defaultValue: 'Cerc: fotografie (punctul GPS)' }),
            )}
          </ul>

          <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-200 pt-1.5 text-[10px] text-slate-500 dark:border-slate-700 dark:text-slate-400">
            <span>
              {t('common:map.legend.hint', {
                defaultValue: 'Apăsați pe culoare pentru a o schimba',
              })}
            </span>
            {customised ? (
              <button
                type="button"
                onClick={resetColors}
                className="flex-shrink-0 font-medium text-forest-700 hover:underline dark:text-forest-300"
              >
                {t('common:action.reset')}
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="rounded-md border border-slate-200 bg-white/95 px-2 py-1 text-[11px] font-medium text-slate-600 shadow-md hover:bg-white dark:border-slate-700 dark:bg-slate-800/95 dark:text-slate-200"
          onClick={() => setOpen(true)}
        >
          {title}
        </button>
      )}
    </div>,
    corner,
  );
}

function hexAlpha(hex: string, alpha: number): string {
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${a}`;
}
